import { Chip, Stack, Button } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { FilterSection } from "@flights/search-ui";
import React, { useRef, useCallback, useEffect } from "react";
import { getFiltersMeta } from "../../../../constants/filtersMeta.js";
import { useAppliedFilters } from "../../../../hooks/search/useAppliedFilters.js";
import { useFiltersReset } from "../../../../hooks/useFiltersReset.js";
import useProfiler from "../../../../hooks/useProfiler.js";
export const FilterChip = ({ filterMeta, resetFilter, chipIndex, onChipDismissed }) => {
    const i18n = useI18n();
    const { copy, storeKeys } = filterMeta;
    if (!copy || !storeKeys)
        return null;
    const handleDismiss = () => {
        onChipDismissed(chipIndex);
        resetFilter(storeKeys);
        if (storeKeys.includes("duration"))
            resetFilter(["maxLayoverDuration"]);
    };
    return (React.createElement(Chip, { dismissible: true, label: copy, onChange: handleDismiss, attributes: {
            "aria-label": `${i18n.trans(t("a11y_flights_filters_chip_remove"))} ${copy}`,
            "data-search-reset-filter": `${storeKeys}`
        } }));
};
export const AppliedFilterChipsList = () => {
    const appliedFilters = useAppliedFilters();
    const i18n = useI18n();
    const { isMobile } = useProfiler();
    const filtersMeta = getFiltersMeta(i18n);
    const { resetFilter, resetAllFilters } = useFiltersReset();
    const prevFiltersLengthRef = useRef(appliedFilters.length);
    const lastDismissedIndexRef = useRef(-1);
    const filtersContainerRef = useRef(null);
    const focusFirstElement = useCallback(() => {
        const parentContainer = document.querySelector("#search_filters_content_wrapper");
        if (parentContainer) {
            const focusableElements = parentContainer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }
    }, []);
    const handleChipDismissed = useCallback((index) => {
        lastDismissedIndexRef.current = index;
        prevFiltersLengthRef.current = appliedFilters.length;
        setTimeout(() => {
            if (appliedFilters.length - 1 === 0) {
                focusFirstElement();
            }
            else {
                const chipButtons = document.querySelectorAll("[data-search-reset-filter]");
                const nextIndex = Math.min(index, chipButtons.length - 1);
                if (chipButtons.length > 0 && nextIndex >= 0) {
                    chipButtons[nextIndex].focus();
                }
            }
        }, 0);
    }, [appliedFilters.length, focusFirstElement]);
    const handleResetAllFilters = useCallback(() => {
        resetAllFilters();
        setTimeout(focusFirstElement, 0);
    }, [resetAllFilters, focusFirstElement]);
    useEffect(() => {
        if (appliedFilters.length < prevFiltersLengthRef.current) {
            if (appliedFilters.length === 0) {
                setTimeout(focusFirstElement, 0);
            }
            else if (lastDismissedIndexRef.current !== -1) {
                setTimeout(() => {
                    const chipButtons = document.querySelectorAll("[data-search-reset-filter]");
                    const nextIndex = Math.min(lastDismissedIndexRef.current, chipButtons.length - 1);
                    if (chipButtons.length > 0 && nextIndex >= 0) {
                        chipButtons[nextIndex].focus();
                    }
                }, 0);
            }
        }
        prevFiltersLengthRef.current = appliedFilters.length;
    }, [appliedFilters.length, focusFirstElement]);
    return appliedFilters.length > 0 ? (React.createElement(FilterSection, { attributes: { "data-testid": "applied_filters_chips_list" }, title: {
            text: i18n.trans(t("flights_filters_selected_title")),
            tagName: "h3",
            attributes: { id: "applied_filters" },
            variant: "strong_2"
        } },
        React.createElement("div", { ref: filtersContainerRef },
            React.createElement(Stack, { direction: "row", alignItems: "center", gap: 1 },
                appliedFilters.map((filter, index) => (React.createElement(FilterChip, { key: `${filter}-${index}`, filterMeta: filtersMeta[filter], resetFilter: resetFilter, chipIndex: index, onChipDismissed: handleChipDismissed }))),
                React.createElement(Button, { text: i18n.trans(t("flights_filters_selected_reset")), variant: "tertiary", type: "button", size: isMobile() ? "large" : "medium", attributes: { "data-testid": "search_filters_selected_reset" }, onClick: handleResetAllFilters }))))) : null;
};
